import type { DataItemId } from '@pn/core/domain/data';
import { isNil } from 'lodash-es';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export type LocationState = {
  /**
   * Anything but `false` will trigger useAutoCenterMapOnSelectedItem.
   */
  autoZoom?: boolean;
  /**
   * Item ID is required when clicking on a list element to distinguish from
   * clicks on its source layer (e.g. well list vs all wells).
   */
  itemId?: string;
  /**
   * Additional IDs selected alongside the primary `id` (e.g. related events).
   */
  ids?: DataItemId[];
} | null;

type NavigateToParams = {
  dataType: string | undefined;
  id: DataItemId | undefined;
  state?: LocationState;
  clearPanel?: boolean;
};

export function useNavigation(base = '') {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  return {
    navigateTo: React.useCallback(
      ({ dataType, id, state, clearPanel }: NavigateToParams) => {
        const root = isNil(dataType) || isNil(id) ? '/' : `/${dataType}/${id}`;

        const panel =
          isNil(id) || clearPanel ? '' : getPanelSuffix(pathname, base);

        return navigate(base + root + panel, {
          state: {
            autoZoom: state?.autoZoom ?? false,
            itemId: state?.itemId,
            ids: state?.ids,
          },
        });
      },
      [base, pathname, navigate]
    ),
  };
}

function getPanelSuffix(pathname: string, base = ''): string {
  const path = base ? pathname.slice(base.length) : pathname;
  const segments = path.split('/'); // ['', dataType, itemId, panel, subpanel]
  if (segments.length > 3) {
    return '/' + segments.slice(3).join('/');
  }
  return '';
}
